import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { Cancel, Edit, Error as ErrorIcon, Save, Warning } from "@material-ui/icons";
import { Input } from "antd";
import { setWorkspaceIdToEdit } from "store/features/userSettings/userSettingsSlice";

import { useUser } from "hooks";

import { IDashboardGroup } from "models/dashboard";
import { Dashboard, WorkspaceType } from "models/workspace";

import { Tooltip } from "components/base";
import DashboardTable from "components/dashboard/DashboardTable/DashboardTable";
import useDashboards from "components/dashboard/hooks/useDashboards";
import { useDeleteDashboard } from "components/dashboard/hooks/useDeleteDashboard";
import { useHideDashboard } from "components/dashboard/hooks/useHideDashboard";
import { IconSpinner } from "components/icons";
import { IWorkspaceModal } from "components/user-settings/WorkspacesList";
import WorkspaceInput from "components/workspace/WorkspaceInput";
import {
  WORKSPACE_TYPE_OPTIONS,
  workspaceTypeIcon
} from "components/workspace/WorkspaceTypeDisplay";
import useWorkspace from "components/workspace/hooks/useWorkspace";

import { useAddDashboardToWorkspace } from "../../../dashboard/hooks/useAddDashboardToWorkspace";
import { useCloneDashboard } from "../../../dashboard/hooks/useCloneDashboard";
import { useScrollToHighlighted } from "../../../dashboard/hooks/useScrollToHighlighted";
import "./WorkspaceManagement.scss";
import {
  ContentContainer,
  DashboardsContainer,
  DashboardsLoadingWrapper,
  ErrorContainer,
  FormContainer,
  FormInput,
  FormInputLabel,
  RootContainer,
  SectionBody,
  SelectWrapper,
  SpinnerContainer,
  WarningContainer,
  WorkspaceActionButton
} from "./WorkspaceManagement.styles";
import { useAddWorkspace } from "./hooks/useAddWorkspace";
import { useCurrentWorkspaceId } from "./hooks/useCurrentWorkspaceId";
import { useFormSession } from "./hooks/useFormSession";
import { useUpdateWorkspace } from "./hooks/useUpdateWorkspace";
import {
  getActiveDashboardList,
  getAvailableDashboards,
  groupDashboardsByType
} from "./utils/dashboardUtils";

type WorkspaceManagementPropsT = {
  setWorkspaceModal: (workspaceModal: IWorkspaceModal) => void;
  workspaceModal: IWorkspaceModal;
};

export default function WorkspaceManagement(props: WorkspaceManagementPropsT) {
  const { workspaceModal, setWorkspaceModal } = props;

  const selectedWorkspaceId = useCurrentWorkspaceId();
  const { user, isAtLeastPowerUser } = useUser();
  const dispatch = useDispatch();

  // The create dialog and the inline edit form fail independently and each renders its
  // own error, so they must not share one state field.
  const [createWorkspaceError, setCreateWorkspaceError] = useState("");
  // Tagged with the workspace it was raised for, which guards the frame between an error being
  // stored and a workspace switch clearing it: the reset effect below runs after paint, so
  // without the tag that error would render once under the newly selected workspace.
  const [editWorkspaceError, setEditWorkspaceError] =
    useState<Nullable<{ workspaceId: string; message: string }>>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [highlightedDashboardId, setHighlightedDashboardId] = useState<string | null>(
    null
  );

  // Cancel stays enabled during an in-flight create, and the user can switch workspace mid-save,
  // so both mutations can resolve against a form that has moved on. Verified in a browser: a
  // create landing after Cancel toasts without navigating, and a save failing after a workspace
  // switch toasts without appearing under the new workspace.
  //
  // The edit session is keyed on the workspace as well as on editing so that a switch ends it in
  // the same commit, ahead of the reset effect below. That ordering only matters for the frame
  // between the two, which React Testing Library cannot observe — it flushes effects inside act —
  // and which the browser runs did not catch either, since the reset effect had already closed
  // the session by the time the response landed. Defensive, not demonstrated.
  //
  // Note the reopened-dialog cost this does not address: addWorkspace.isLoading stays true for an
  // abandoned create, so reopening the dialog shows a disabled, spinning Create button. That is
  // unchanged from main and is not this component's to decide.
  const createSession = useFormSession(workspaceModal.isVisible);
  const editSession = useFormSession(isEditing ? selectedWorkspaceId : null);

  const addWorkspace = useAddWorkspace({
    onSuccess: (workspaceId, title) => {
      // The workspace was created whatever the user has done since, so say so — but don't
      // close a dialog they have since reopened and started typing in, and don't drag them
      // to a workspace they believe they cancelled.
      if (createSession.isStale()) {
        toast.success(`Workspace "${title}" was created.`);
        return;
      }

      setWorkspaceModal({ isVisible: false });
      dispatch(setWorkspaceIdToEdit(workspaceId));
    },
    onError: (message) => {
      // Rendering this inline once the submitting dialog is gone would either blame a later
      // attempt for it or write to an unmounted component, so the toast carries it instead.
      // While that dialog is still open the inline error says the same thing, and a toast
      // would only repeat it.
      if (createSession.isStale()) {
        toast.error(message);
        return;
      }

      setCreateWorkspaceError(message);
    }
  });

  const updateWorkspace = useUpdateWorkspace({
    onSuccess: (title) => {
      // The change was saved either way, and the refetch will alter the title under the user, so
      // it cannot pass silently — but collapsing the form would discard an edit they have since
      // restarted or begun on another workspace. It has to name the workspace: the form on screen
      // by now is a different or restarted one, holding text that was *not* saved.
      if (editSession.isStale()) {
        toast.success(`Workspace "${title}" was saved.`);
        return;
      }

      setEditWorkspaceError(null);
      setIsEditing(false);
    },
    onError: (message) => {
      // A failure with no form left to render it must not overwrite an error the user can
      // actually see, so it is only ever toasted.
      if (editSession.isStale()) {
        toast.error(message);
        return;
      }

      setEditWorkspaceError({ workspaceId: selectedWorkspaceId, message });
    }
  });

  const { data: selectedWorkspace } = useWorkspace(selectedWorkspaceId);
  const { data: dashboardData, loading } = useDashboards();

  useScrollToHighlighted(highlightedDashboardId, true);

  const addDashboardToWorkspace = useAddDashboardToWorkspace({
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  });

  const hideDashboard = useHideDashboard();

  const deleteDashboard = useDeleteDashboard();

  const workspace = selectedWorkspace?.workspace;
  const dashboards = dashboardData?.dashboards || [];

  // Seeded from the workspace when Edit is clicked, not on mount — the effect below
  // blanks both on every workspace change, including the first.
  const [editedTitle, setEditedTitle] = useState("");
  const [editedType, setEditedType] = useState("");

  // This component is not remounted when the selected workspace changes, so edit state has
  // to be dropped explicitly. The render below already hides an error belonging to another
  // workspace; this is what stops it coming back when the user returns to that workspace,
  // and what prevents a pending title from being saved onto the newly selected one.
  useEffect(() => {
    setEditWorkspaceError(null);
    setIsEditing(false);
    setEditedTitle("");
    setEditedType("");
  }, [selectedWorkspaceId]);

  const canEdit = (!!user?.id && user.id === workspace?.owner) || isAtLeastPowerUser;

  const availableDashboards = getAvailableDashboards(dashboards, workspace);
  const dashboardGroups = groupDashboardsByType(availableDashboards, "type");

  const activeDashboardList = getActiveDashboardList(workspace, dashboards);
  const activeDashboards: IDashboardGroup[] = [
    {
      groupKey: "active",
      groupTitle: "Selected Dashboards",
      order: 0,
      help: "Dashboards already added to the workspace",
      dashboards: activeDashboardList
    }
  ];

  const handleUpdateWorkspace = useCallback(
    (title: string, type: WorkspaceType) => {
      if (!workspace) return;

      // Otherwise a corrected retry runs with the previous failure still on screen, and it
      // only clears if this attempt succeeds.
      setEditWorkspaceError(null);
      editSession.recordSubmission();
      updateWorkspace.mutate(workspace, selectedWorkspaceId, title, type);
    },
    [editSession, updateWorkspace, workspace, selectedWorkspaceId]
  );

  const cloneDashboard = useCloneDashboard({
    highlightConfig: {
      setHighlightedDashboard: setHighlightedDashboardId
    }
  });

  const handleCloneDashboard = async (
    dashboard: Dashboard,
    title: string,
    type: string
  ): Promise<void> => {
    await cloneDashboard.mutate(dashboard, title, type, workspace);
  };

  const handleCancelChanges = () => {
    setEditedTitle(workspace?.title || "");
    setEditedType(workspace?.type || "");
    setEditWorkspaceError(null);
    setIsEditing(false);
  };

  // Create is self-contained — it must stay reachable while the selected workspace's
  // query is unresolved, otherwise clicking Add Workspace renders nothing and the dialog
  // pops open unprompted once the query lands.
  const createWorkspaceDialog = (
    <WorkspaceInput
      error={createWorkspaceError}
      header={"Create Workspace"}
      isLoading={addWorkspace.isLoading}
      onCancel={() => {
        // Cancel is the only close path that can leave a failure behind, so it clears it. Doing
        // it here rather than on the next open matters because antd re-renders the dialog body
        // in the same commit that reopens it, which would otherwise show the old message for a
        // frame. It does not affect the leave animation: antd freezes the body on close, so the
        // fade always shows whatever was last rendered.
        setCreateWorkspaceError("");
        setWorkspaceModal({ isVisible: false });
      }}
      onConfirm={{
        label: "Create",
        update: (title: string, type: WorkspaceType) => {
          setCreateWorkspaceError("");
          // Must precede mutate, which can report an outcome before it returns.
          createSession.recordSubmission();
          addWorkspace.mutate(title, type);
        }
      }}
      visible={workspaceModal.isVisible}
    />
  );

  if (!workspace) {
    return (
      <RootContainer>
        <SpinnerContainer>
          <IconSpinner></IconSpinner>
        </SpinnerContainer>
        {createWorkspaceDialog}
      </RootContainer>
    );
  }

  // Extract the "Selected Dashboards" group
  const selectedDashboardsGroup = activeDashboards.find(
    (group) => group.groupTitle === "Selected Dashboards"
  );

  const selectedDashboards = selectedDashboardsGroup?.dashboards || [];

  // Filter out the "Selected Dashboards" group to get other dashboards
  const otherDashboards = dashboardGroups
    .filter((group) => group.groupTitle !== "Selected Dashboards")
    .flatMap((group) => group.dashboards);

  // Warnings
  const hasNoSelectedDashboards: boolean = canEdit && selectedDashboards.length === 0;

  const isTitleMissing: boolean =
    canEdit && isEditing && (!editedTitle || !editedTitle.trim());

  const hasPersonalDashboardsOnNonPersonalWorkspace: boolean =
    canEdit &&
    workspace?.type !== "Personal" &&
    workspace?.dashboards?.some((d) => d.type === "Personal");

  const isChangingTypeToOrganization: boolean =
    canEdit &&
    isEditing &&
    editedType === "Organization" &&
    workspace?.type !== "Organization";

  const isChangingTypeToPersonal: boolean =
    canEdit && isEditing && editedType === "Personal" && workspace?.type !== "Personal";

  return (
    <RootContainer>
      <ContentContainer>
        <FormContainer>
          <FormInput>
            <FormInputLabel>Title</FormInputLabel>
            <Input
              id="Title"
              placeholder="Title"
              value={isEditing ? editedTitle : workspace?.title || ""}
              onChange={(e) => setEditedTitle(e.target.value)}
              disabled={!isEditing}
            />
          </FormInput>
          <FormInput>
            <FormInputLabel>Type</FormInputLabel>
            {isAtLeastPowerUser ? (
              <SelectWrapper
                popupClassName="modal-select"
                value={isEditing ? editedType : workspace.type}
                onChange={(value) => setEditedType(value)}
                options={WORKSPACE_TYPE_OPTIONS}
                disabled={!isEditing}
              />
            ) : (
              // A standard user gets a read-only field rather than a select — see
              // WorkspaceTypeDisplay for why the two forms agree on that. It shows the
              // workspace's own type, not a fixed "Personal": a standard user can be viewing an
              // org-level workspace, which they may use but never create or change the type of,
              // and the field has to say what it actually is.
              <Input
                id="Type"
                placeholder="Type"
                aria-label="Type"
                prefix={workspaceTypeIcon(workspace.type)}
                value={workspace.type}
                disabled={true}
              />
            )}
          </FormInput>
          <FormInput>
            <FormInputLabel>Owner</FormInputLabel>
            <Input
              id="Owner"
              placeholder="Owner"
              value={workspace?.ownerName || ""}
              disabled={true}
            />
          </FormInput>
          <FormInput>
            {selectedWorkspaceId &&
              canEdit &&
              !(
                workspace.type === "Personal" &&
                (workspace.title === "Explore" || workspace.title === "Midstream")
              ) && (
                <>
                  <FormInputLabel>&nbsp;</FormInputLabel>
                  {isEditing ? (
                    <div className={"workspace_management_actions-wrapper"}>
                      <WorkspaceActionButton
                        data-testid="save-button"
                        disabled={updateWorkspace.isLoading || !editedTitle.trim()}
                        onClick={() =>
                          handleUpdateWorkspace(
                            editedTitle.trim(),
                            editedType as WorkspaceType
                          )
                        }>
                        <Save fontSize="large" />
                      </WorkspaceActionButton>
                      <WorkspaceActionButton
                        data-testid="cancel-button"
                        danger
                        onClick={handleCancelChanges}>
                        <Cancel fontSize="large" />
                      </WorkspaceActionButton>
                    </div>
                  ) : (
                    <Tooltip placement="top" title="Edit Workspace Details">
                      <WorkspaceActionButton
                        data-testid="edit-button"
                        onClick={() => {
                          setIsEditing(true);
                          setEditedTitle(workspace.title);
                          setEditedType(workspace.type);
                        }}>
                        <Edit fontSize="large" />
                      </WorkspaceActionButton>
                    </Tooltip>
                  )}
                </>
              )}
          </FormInput>
        </FormContainer>

        {editWorkspaceError && editWorkspaceError.workspaceId === selectedWorkspaceId && (
          <ErrorContainer>
            <ErrorIcon style={{ top: 3 }} /> {editWorkspaceError.message}
          </ErrorContainer>
        )}
        {hasNoSelectedDashboards && (
          <WarningContainer>
            <Warning style={{ top: 3 }} /> No dashboards selected.
          </WarningContainer>
        )}
        {isTitleMissing && (
          <WarningContainer>
            <Warning style={{ top: 3 }} /> Title is required.
          </WarningContainer>
        )}
        {isChangingTypeToOrganization && (
          <WarningContainer>
            <Warning style={{ top: 3 }} /> Any personal dashboards will be removed from
            the workspace when changing the workspace type to Organization.
          </WarningContainer>
        )}
        {isChangingTypeToPersonal && (
          <WarningContainer>
            <Warning style={{ top: 3 }} /> Changing the workspace type can impact users
            who previously had access to the workspace.
          </WarningContainer>
        )}
        {hasPersonalDashboardsOnNonPersonalWorkspace && (
          <WarningContainer>
            <Warning style={{ top: 3 }} /> It is not recommended to use Personal
            dashboards on {workspace.type} workspaces
          </WarningContainer>
        )}

        <>
          <SectionBody>
            {selectedDashboards.length > 0 && (
              <DashboardTable
                dashboardList={selectedDashboards}
                hideDashboard={
                  canEdit
                    ? (dashboardId: string) =>
                        hideDashboard.mutate(dashboardId, workspace)
                    : null
                }
                highlightedDashboardId={highlightedDashboardId}
                setHighlightedDashboard={setHighlightedDashboardId}
                deleteDashboard={(dashboardId: string) =>
                  deleteDashboard.mutate(dashboardId, workspace)
                }
                cloneDashboard={canEdit ? handleCloneDashboard : undefined}
                tableSettings={{
                  title: "Selected Dashboards"
                }}
                workspace={workspace}
              />
            )}
          </SectionBody>
          {canEdit && (
            <>
              {loading && (
                <DashboardsLoadingWrapper>
                  <IconSpinner />
                </DashboardsLoadingWrapper>
              )}

              <SectionBody>
                <DashboardsContainer>
                  <DashboardTable
                    dashboardList={otherDashboards}
                    addDashboard={canEdit ? addDashboardToWorkspace.mutate : null}
                    deleteDashboard={(dashboardId: string) =>
                      deleteDashboard.mutate(dashboardId, workspace)
                    }
                    highlightedDashboardId={highlightedDashboardId}
                    setHighlightedDashboard={setHighlightedDashboardId}
                    cloneDashboard={canEdit ? handleCloneDashboard : undefined}
                    tableSettings={{
                      title: "Available Dashboards"
                    }}
                    workspace={workspace}
                  />
                </DashboardsContainer>
              </SectionBody>
            </>
          )}
        </>
      </ContentContainer>

      {createWorkspaceDialog}
    </RootContainer>
  );
}
